import React, { useRef, useState, useEffect } from "react";
import styles from "./style.module.scss";
import { Badge } from "@amzn/awsui-components-react";
import { SideNavigationProps } from "@amzn/awsui-components-react/polaris/side-navigation";
import { useAppSelector } from "app/store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Team } from "models/schemas";
import { useHeaderTeamNameSelector } from "pages/team_settings/hooks";
import { useUserNameSelector } from "pages/tasks/hooks";
import { useGetApprovalStatQuery } from "pages/reviews/endpoints";
import { ApprovalSearchType } from "pages/reviews/definitions";

type NavItem = {
  type: "link" | "divider";
  text?: string;
  href?: string;
  feature?: string;
  urlFlag?: string;
  visibleToMember?: boolean;
  visibleToGuest?: boolean;
};

/**
 * Hook for handling side navigation events
 */
export const useOnFollowHandler = () => {
  const navigate = useNavigate();

  return (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    if (!event.detail.external) {
      event.preventDefault();
      navigate(event.detail.href, { replace: true });
    }
  };
};

export const useOrderedNavItems = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const teamName = useHeaderTeamNameSelector();
  const allTeams = useAppSelector((state) => state.teamSettings?.allTeams);
  const { userName: currentUser } = useUserNameSelector();
  const { data: pendingApprovalsData } = useGetApprovalStatQuery(
    {
      teamName,
      searchType: ApprovalSearchType.APPROVER_PENDING_APPROVALS,
      user: currentUser,
    },
    {
      pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
      skip: !teamName || teamName === "guest" || !currentUser,
    },
  );
  const pendingCount = pendingApprovalsData?.count;
  const showBadge = pendingCount !== undefined && pendingCount > 0;
  const badgeText = showBadge
    ? pendingCount! > 99
      ? "99+"
      : String(pendingCount)
    : "";

  const prevCountRef = useRef<number | undefined>(undefined);
  const [glowing, setGlowing] = useState(false);

  useEffect(() => {
    if (pendingCount !== undefined && prevCountRef.current !== pendingCount) {
      setGlowing(true);
      const t = setTimeout(() => setGlowing(false), 1500);
      prevCountRef.current = pendingCount;
      return () => clearTimeout(t);
    }
    prevCountRef.current = pendingCount;
  }, [pendingCount]);

  const isLoading = !allTeams || !teamName;

  const selectedTeamFeatures = new Set<string>(
    (allTeams && "teams" in allTeams ? (allTeams as any).teams : []).find(
      (team: Team) => team?.teamId === teamName,
    )?.enabledFeatures || [],
  );

  const orderedNavItems: NavItem[] = [
    {
      type: "link",
      text: t("pages.audits.title"),
      href: "/audits/",
      feature: "AUDIT",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Notices",
      href: "/notices/",
      feature: "NOTICE",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Litigations",
      href: "/litigations/",
      feature: "LITIGATION",
      visibleToGuest: true,
    },
    { type: "divider", visibleToMember: true, visibleToGuest: true },
    {
      type: "link",
      text: t("pages.tasks.title"),
      href: "/tasks",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.reviews.title"),
      href: "/reviews",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.drafts.title"),
      href: "/drafts",
      feature: "FILE_SCAN",
    },
    {
      type: "link",
      text: t("pages.data.title"),
      href: "/data/",
      visibleToMember: true,
    },
    {
      // TODO: On feature release, change href to "/agent-work" and remove urlFlag
      type: "link",
      text: t("pages.agentWork.title"),
      href: "/agent-work?agentWork=true",
      feature: "AGENT_WORK",
      urlFlag: "agentWork",
    },
    {
      type: "link",
      text: t("pages.events.title"),
      href: "/events/",
      feature: "EVENT",
      visibleToGuest: false,
    },
    { type: "divider", visibleToMember: true },
    {
      type: "link",
      text: "Dashboard",
      href: "/dashboard/",
      feature: "DASHBOARD",
    },

    {
      type: "link",
      text: t("pages.gaia_ai.chatbot.title"),
      href: "/gaia-ai/chatbot",
      feature: "CHATBOT",
    },
    {
      type: "link",
      text: t("pages.team_files.title"),
      href: "/team-files",
      visibleToMember: true,
    },
    {
      type: "link",
      text: t("pages.team_settings.title"),
      href: "/team",
      visibleToMember: true,
    },
  ];

  const urlParams = new URLSearchParams(location.search);

  if (urlParams.get("agentWork") === "true") {
    sessionStorage.setItem("flag:agentWork", "true");
  } else if (urlParams.get("agentWork") === "false") {
    sessionStorage.removeItem("flag:agentWork");
  }

  const hasUrlFlag = (flag: string) =>
    urlParams.get(flag) === "true" ||
    sessionStorage.getItem(`flag:${flag}`) === "true";

  const isNavItemVisible = (item: NavItem): boolean => {
    if (teamName !== "guest" && item.visibleToMember) return true;
    if (teamName === "guest" && item.visibleToGuest) return true;
    if (item.feature && item.urlFlag) {
      return selectedTeamFeatures.has(item.feature) && hasUrlFlag(item.urlFlag);
    }
    if (item.feature) return selectedTeamFeatures.has(item.feature);
    if (item.urlFlag) return hasUrlFlag(item.urlFlag);
    return false;
  };

  const navigationItems = orderedNavItems
    .filter(isNavItemVisible)
    .map((item): SideNavigationProps.Item => {
      if (item.type === "divider") {
        return { type: "divider" };
      }
      const text =
        item.feature === "CHATBOT"
          ? React.createElement(
              React.Fragment,
              null,
              item.text,
              " ",
              React.createElement(Badge, null, "beta"),
            )
          : item.href === "/reviews" && showBadge
            ? React.createElement(
                React.Fragment,
                null,
                item.text,
                " ",
                React.createElement(
                  "span",
                  { className: glowing ? styles.glow : "" },
                  React.createElement(Badge, { color: "red" }, badgeText),
                ),
              )
            : item.text!;
      return {
        type: "link",
        text: text as any,
        href: item.href!,
      };
    });

  return { items: navigationItems, isLoading };
};

export const useNavConfig = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { items, isLoading } = useOrderedNavItems();

  return {
    header: {
      href: "",
      text: t("left_nav.title"),
    } as SideNavigationProps.Header,
    items: isLoading ? [] : items,
    activeHref: location.pathname,
  };
};
